<template>
  <div
    class="saved-states"
    :class="{mobile: $screen.is('sm')}">
    <template v-if="availableStates && availableStates.length >0 ">
      <singleselect
        :value="selectedState"
        :loading="loading"
        :options="availableStates"
        class="state-select"
        label="name"
        header-placeholder="Select a saved filter configuration"
        track-by="id"
        :return-as-object="true"
        @input="setSelectedState">
        <template #after-option="{ option, toggleOpen }">
          <template v-if="canEditStates">
            <b-button
              class="icon-button"
              @click="toggleOpen(); renameState(option)">
              <i class="material-icons">edit</i>
            </b-button>
            <b-button
              class="icon-button"
              @click="toggleOpen(); copyState(option)">
              <i class="material-icons">content_copy</i>
            </b-button>
            <b-button
              class="icon-button"
              @click="toggleOpen(); deleteState(option)">
              <i class="material-icons delete-state">delete</i>
            </b-button>
          </template>
        </template>
      </singleselect>
    </template>
    <template v-if="dirty">
      <template v-if="selectedState">
        <template v-if="canEditStates">
          <button
            class="button"
            @click="updateCurrentState">
            Save updates
          </button>
        </template>
        <template v-if="canCreateStates">
          <button
            class="button"
            @click="launchNewStateModal">
            Save as new configuration
          </button>
        </template>
        <button
          class="button"
          @click="resetChanges">
          Reset changes
        </button>
      </template>
      <template v-else>
        <template v-if="canCreateStates">
          <button
            class="button"
            @click="launchNewStateModal">
            Save current filter selection
          </button>
        </template>
      </template>
    </template>
    <b-modal
      v-model="deletingState">
      <header class="modal-card-head">
        <p class="modal-card-title">
          Delete Saved Filter Configuration
        </p>
      </header>
      <section class="modal-card-body">
        <p class="delete-confirmation">
          Are you sure you want to delete {{ stateToManage?.name }}?
        </p>
        <div class="actions">
          <b-button @click="cancelDeleteState">
            Cancel
          </b-button>
          <b-button
            type="is-primary"
            @click="confirmDeleteState">
            Confirm
          </b-button>
        </div>
      </section>
    </b-modal>
    <b-modal
      v-model="copyingState">
      <header class="modal-card-head">
        <p class="modal-card-title">
          Duplicate Saved Filter Configuration
        </p>
      </header>
      <section class="modal-card-body">
        <b-field label="Name">
          <b-input
            v-model="newStateName"
            placeholder="New filter preset" />
        </b-field>
        <div class="actions">
          <b-button @click="cancelCopyState">
            Cancel
          </b-button>
          <b-button
            type="is-primary"
            @click="confirmCopyState">
            Save
          </b-button>
        </div>
      </section>
    </b-modal>
    <b-modal
      v-model="renamingState">
      <header class="modal-card-head">
        <p class="modal-card-title">
          Rename Saved Filter Configuration
        </p>
      </header>
      <section class="modal-card-body">
        <b-field label="Name">
          <b-input
            v-model="newStateName"
            placeholder="New filter preset" />
        </b-field>
        <div class="actions">
          <b-button @click="cancelRenameState">
            Cancel
          </b-button>
          <b-button
            type="is-primary"
            @click="confirmRenameState">
            Save
          </b-button>
        </div>
      </section>
    </b-modal>
    <b-modal
      v-model="nameModalOpen">
      <header class="modal-card-head">
        <p class="modal-card-title">
          New Saved Filter Configuration
        </p>
      </header>
      <section class="modal-card-body">
        <b-field label="Name">
          <b-input
            v-model="newStateName"
            placeholder="New filter preset" />
        </b-field>
        <div class="actions">
          <b-button @click="cancelNewState">
            Cancel
          </b-button>
          <b-button
            type="is-primary"
            @click="confirmNewState">
            Save
          </b-button>
        </div>
      </section>
    </b-modal>
  </div>
</template>

<script>
  import singleselect from '@/components/ui/singleselect.vue'
  import { mapGetters } from 'vuex'

  export default {
    name: 'SavedStates',
    components: {
      singleselect,
    },
    props: {
      value: {
        type: Object,
        required: true,
      },
      dashboardKey: {
        type: String,
        required: true,
      },
    },
    data () {
      return {
        availableStates: [],
        loading: false,
        nameModalOpen: false,
        newStateName: '',
        renamingState: false,
        copyingState: false,
        deletingState: false,
        stateToManage: null,
      }
    },
    methods: {
      async loadAvailableStates () {
        this.loading = true
        this.availableStates = await this.$apis.dashboardStates.listStates(this.dashboardKey)
        this.loading = false
      },
      launchNewStateModal () {
        this.newStateName = ''
        this.nameModalOpen = true
      },
      cancelNewState () {
        this.nameModalOpen = false
      },
      async confirmNewState () {
        this.loading = true
        this.nameModalOpen = false
        let definedSelections = {}
        Object.keys(this.filterSelections || {}).forEach((key) => {
          if (this.filterSelections[key]) {
            definedSelections[key] = this.filterSelections[key]
          }
        })
        let newState = await this.$apis.dashboardStates.createState(
          this.dashboardKey,
          {
            name: this.newStateName,
            filter_selections: definedSelections,
          }
        )
        this.setSelectedState(newState)
        this.loadAvailableStates()
      },
      async renameState (state) {
        this.renamingState = true
        this.stateToManage = state
        this.newStateName = `${state.name}`
      },
      async cancelRenameState () {
        this.renamingState = false
        this.stateToManage = null
        this.newStateName = ''
      },
      async confirmRenameState () {
        this.loading = true
        let newStateFuture = this.$apis.dashboardStates.updateState(
          this.dashboardKey,
          this.stateToManage.id,
          {
            ...this.stateToManage,
            name: this.newStateName,
          }
        )
        this.stateToManage = null
        this.renamingState = false
        this.newStateName = ''
        let newState = await newStateFuture
        this.$emit('input', {
          ...this.value,
          selectedState: newState,
          filterSelections: newState.filter_selections,
          stateDirty: false,
        })
        this.loadAvailableStates()
      },
      async copyState (state) {
        this.copyingState = true
        this.stateToManage = state
        this.newStateName = `${state.name} (Copy)`
      },
      async cancelCopyState () {
        this.copyingState = false
        this.stateToManage = null
        this.newStateName = ''
      },
      async confirmCopyState () {
        this.loading = true
        let newStateFuture = this.$apis.dashboardStates.createState(
          this.dashboardKey,
          {
            ...this.stateToManage,
            id: undefined,
            name: this.newStateName,
          }
        )
        this.stateToManage = null
        this.copyingState = false
        this.newStateName = ''
        let newState = await newStateFuture
        this.$emit('input', {
          ...this.value,
          selectedState: newState,
          filterSelections: newState.filter_selections,
          stateDirty: false,
        })
        this.loadAvailableStates()
      },
      async deleteState (state) {
        this.deletingState = true
        this.stateToManage = state
      },
      async cancelDeleteState () {
        this.deletingState = false
        this.stateToManage = null
      },
      async confirmDeleteState () {
        this.loading = true
        let newStateFuture = this.$apis.dashboardStates.deleteState(
          this.dashboardKey,
          this.stateToManage.id
        )
        this.stateToManage = null
        this.deletingState = false
        await newStateFuture
        this.$emit('input', {
          selectedState: null,
          filterSelections: {},
          stateDirty: false,
        })
        this.loadAvailableStates()
      },
      async updateCurrentState () {
        this.loading = true
        let newState = await this.$apis.dashboardStates.updateState(
          this.dashboardKey,
          this.selectedState.id,
          {
            ...this.selectedState,
            filter_selections: this.filterSelections,
          }
        )
        this.$emit('input', {
          ...this.value,
          selectedState: newState,
          filterSelections: newState.filter_selections,
          stateDirty: false,
        })
        this.loadAvailableStates()
      },
      resetChanges () {
        this.$emit('input', {
          ...this.value,
          selectedState: this.selectedState,
          filterSelections: this.selectedState.filter_selections,
          stateDirty: false,
        })
      },
      setSelectedState (newState) {
        if (this.$route.query.state !== String(newState.id)) {
          this.$router.push({
            query: {
              ...this.$route.query,
              state: newState.id,
            },
          })
        }
        this.$emit('input', {
          ...this.value,
          selectedState: newState,
          filterSelections: newState.filter_selections,
          stateDirty: false,
        })
      },
      async initialize () {
        await this.loadAvailableStates()
        let urlState = this.availableStates.find((s) => this.$route.query.state === String(s.id))
        if (urlState) {
          this.setSelectedState(urlState)
        }
      },
    },
    computed: {
      ...mapGetters({
        userPermissions: 'auth/user/userPermissions',
      }),
      canCreateStates () {
        return this.userPermissions.includes('CREATE_DASHBOARD_SAVED_STATES')
      },
      canEditStates () {
        return this.userPermissions.includes('MANAGE_DASHBOARD_SAVED_STATES')
      },
      filterSelections () {
        return this.value.filterSelections
      },
      selectedState () {
        return this.value.selectedState
      },
      dirty () {
        return this.value.stateDirty
      },
    },
    watch: {
      dashboardKey () {
        this.loadAvailableStates()
      },
    },
    mounted () {
      this.initialize()
    },
  }
</script>

<style scoped lang="scss">
.saved-states {
  display: flex;
  align-items: center;
  justify-content: flex-begin;
  flex-grow: 1;

  &.mobile {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  > * {
    box-sizing: border-box;
    margin: 4px 8px;
  }
  > .state-select {
    min-width: 374px;
  }
}
.actions {
  display: flex;
  padding: 8px;
  width: 100%;
  justify-content: flex-end;
}
.actions > button {
  margin: 4px 12px;
}
.material-icons {
  font-size: 16px;
}
.material-icons.delete-state {
  color: $ui-negative;
}
.icon-button {
  padding: 4px 10px !important;
  padding-top: 8px !important;
  margin: 2px !important;
  display: flex;
  align-items: center;
}
.delete-confirmation {
  font-size: 16px;
}
</style>
